export interface WorkMonthState {
    month: number;
    year: number;
}

// In-memory cache để giảm thiểu tối đa việc đọc từ localStorage
let memoryCache: {
    schoolId: string | null;
    state: WorkMonthState;
} | null = null;

const getStorageKey = (type: 'month' | 'year'): string => {
    let schoolId = 'default';
    try {
        schoolId = localStorage.getItem('schoolId') || 'default';
    } catch {
        // Fallback an toàn nếu trình duyệt chặn truy cập storage
    }
    return `user_working_${type}_${schoolId}`;
};

/**
 * Lấy tháng và năm làm việc được lưu trên máy của người dùng.
 * - Được phân vùng theo từng trường học (schoolId), tránh lẫn lộn khi chuyển đổi trường.
 * - Có in-memory cache giúp truy xuất với tốc độ microsecond (O(1)).
 * - Có try-catch an toàn tuyệt đối, tự động fallback về ngày hiện tại nếu có lỗi.
 */
export const getStoredWorkMonth = (): WorkMonthState => {
    let currentSchoolId = 'default';
    try {
        currentSchoolId = localStorage.getItem('schoolId') || 'default';
    } catch {
        // Ignored
    }

    // Nếu đã có trong memory cache cho đúng trường học hiện tại -> trả về ngay lập tức
    if (memoryCache && memoryCache.schoolId === currentSchoolId) {
        return memoryCache.state;
    }

    const now = new Date();
    const currentRealMonth = now.getMonth() + 1;
    const currentRealYear = now.getFullYear();

    let month = currentRealMonth;
    let year = currentRealYear;

    try {
        const storedMonth = localStorage.getItem(getStorageKey('month'));
        const storedYear = localStorage.getItem(getStorageKey('year'));

        if (storedMonth) {
            const parsedM = parseInt(storedMonth, 10);
            if (!isNaN(parsedM) && parsedM >= 1 && parsedM <= 12) {
                month = parsedM;
            }
        }

        if (storedYear) {
            const parsedY = parseInt(storedYear, 10);
            if (!isNaN(parsedY) && parsedY >= 2000 && parsedY <= 2100) {
                year = parsedY;
            }
        }
    } catch {
        // Trình duyệt chặn storage -> dùng tháng/năm thực tế
    }

    const state: WorkMonthState = { month, year };
    memoryCache = {
        schoolId: currentSchoolId,
        state,
    };

    return state;
};

/**
 * Lưu tháng và năm làm việc vào localStorage trên máy người dùng.
 * - Cập nhật đồng thời In-Memory cache và localStorage.
 * - Kiểm tra giá trị trước khi ghi đĩa để tránh I/O dư thừa.
 */
export const setStoredWorkMonth = (month: number, year: number): void => {
    if (month < 1 || month > 12 || year < 2000 || year > 2100) {
        return;
    }

    let currentSchoolId = 'default';
    try {
        currentSchoolId = localStorage.getItem('schoolId') || 'default';
    } catch {
        // Ignored
    }

    // Tránh ghi đè nếu giá trị không thay đổi
    if (
        memoryCache &&
        memoryCache.schoolId === currentSchoolId &&
        memoryCache.state.month === month &&
        memoryCache.state.year === year
    ) {
        return;
    }

    // Cập nhật memory cache trước
    const newState: WorkMonthState = { month, year };
    memoryCache = {
        schoolId: currentSchoolId,
        state: newState,
    };

    // Ghi vào localStorage có try-catch bảo vệ
    try {
        localStorage.setItem(getStorageKey('month'), String(month));
        localStorage.setItem(getStorageKey('year'), String(year));
    } catch {
        // Lưu trên memory cache vẫn hoạt động bình thường nếu storage bị đầy/chặn
    }

    // Phát event để đồng bộ tức thì giữa Header và các màn hình
    try {
        window.dispatchEvent(new CustomEvent('workMonthChanged', { detail: { month, year } }));
    } catch {
        // Ignored
    }
};
